import { ComponentProps, forwardRef } from 'react';
import { cn } from '../../utils/className';

import ChevronDoubleLeftIcon from '../../assets/icons/chevron-double-left.svg?react';
import ChevronDoubleRightIcon from '../../assets/icons/chevron-double-right.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
// Heroicons Mini SVG icons as inline components for better performance

interface TablePaginationProps extends ComponentProps<'div'> {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Number of rows per page */
  rowsPerPage: number;
  /** Total number of items */
  totalItems: number;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when rows per page changes */
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /** Callback when go to page is submitted */
  onGoToPage?: (page: number) => void;
  /** Available rows per page options */
  rowsPerPageOptions?: number[];
  /** Whether the table is loading data */
  isLoading?: boolean;
}

export const TablePagination = forwardRef<HTMLDivElement, TablePaginationProps>(
  (
    {
      currentPage,
      totalPages,
      rowsPerPage,
      totalItems,
      onPageChange,
      onRowsPerPageChange,
      onGoToPage,
      rowsPerPageOptions = [5, 10, 20, 50, 100],
      isLoading = false,
      className,
      ...props
    },
    ref
  ) => {
    const handleFirstPage = () => onPageChange?.(1);
    const handlePreviousPage = () =>
      onPageChange?.(Math.max(1, currentPage - 1));
    const handleNextPage = () =>
      onPageChange?.(Math.min(totalPages, currentPage + 1));
    const handleLastPage = () => onPageChange?.(totalPages);

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    // Disable pagination when loading or when there's no data
    const isPaginationDisabled = isLoading || totalPages === 0;

    return (
      <div
        ref={ref}
        className={cn(
          `bg-surface-primary-support flex w-full flex-col gap-3 px-4 py-3
          md:h-13 md:flex-row md:items-center md:justify-between md:px-6
          md:py-4`,
          className
        )}
        {...props}
      >
        {/* Left section: Rows per page */}
        <div className='flex w-full flex-wrap items-center gap-3 md:flex-1'>
          <span className='size-p2 text-primary whitespace-nowrap'>
            Rows per page
          </span>
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
            disabled={isPaginationDisabled}
            className='size-p2 text-primary border-border-primary h-8 w-10
              rounded border bg-white py-1 disabled:cursor-not-allowed
              disabled:opacity-50'
          >
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className='size-p2 text-primary whitespace-nowrap'>
            {totalItems} total
          </span>
        </div>

        {/* Center section: Pagination controls */}
        <div
          className='flex w-full flex-wrap items-center justify-between gap-3
            md:w-auto md:justify-center md:gap-6'
        >
          <div className='flex items-center gap-1'>
            <button
              onClick={handleFirstPage}
              disabled={isPaginationDisabled || isFirstPage}
              className='border-border-primary disabled:text-disabled flex
                size-8 cursor-pointer items-center justify-center rounded border
                bg-white disabled:cursor-not-allowed disabled:opacity-50'
              aria-label='First page'
            >
              <ChevronDoubleLeftIcon />
            </button>
            <button
              onClick={handlePreviousPage}
              disabled={isPaginationDisabled || isFirstPage}
              className='border-border-primary disabled:text-disabled flex
                size-8 cursor-pointer items-center justify-center rounded border
                bg-white disabled:cursor-not-allowed disabled:opacity-50'
              aria-label='Previous page'
            >
              <ChevronLeftIcon />
            </button>
          </div>

          <span className='size-p2 text-primary whitespace-nowrap'>
            {isPaginationDisabled && totalPages === 0 ? '-' : currentPage} of{' '}
            {isPaginationDisabled && totalPages === 0 ? '-' : totalPages}
          </span>

          <div className='flex items-center gap-1'>
            <button
              onClick={handleNextPage}
              disabled={isPaginationDisabled || isLastPage}
              className='border-border-primary disabled:text-disabled flex
                size-8 cursor-pointer items-center justify-center rounded border
                bg-white disabled:cursor-not-allowed disabled:opacity-50'
              aria-label='Next page'
            >
              <ChevronRightIcon />
            </button>
            <button
              onClick={handleLastPage}
              disabled={isPaginationDisabled || isLastPage}
              className='border-border-primary disabled:text-disabled flex
                size-8 cursor-pointer items-center justify-center rounded border
                bg-white disabled:cursor-not-allowed disabled:opacity-50'
              aria-label='Last page'
            >
              <ChevronDoubleRightIcon />
            </button>
          </div>
        </div>

        {/* Right section: Go to page */}
        <div
          className='flex w-full flex-wrap items-center justify-start gap-2
            md:flex-1 md:justify-end'
        >
          <span className='size-p2 text-primary whitespace-nowrap'>
            Go to page
          </span>
          <input
            type='number'
            min={1}
            max={totalPages}
            disabled={isPaginationDisabled}
            // placeholder='...'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const value = Number(e.currentTarget.value);
                if (value >= 1 && value <= totalPages) {
                  onGoToPage?.(value);
                  e.currentTarget.value = '';
                }
              }
            }}
            className='size-p2 text-primary border-border-primary h-8 w-14
              rounded border bg-white px-2 py-1 text-center
              disabled:cursor-not-allowed disabled:opacity-50
              [&::-webkit-inner-spin-button]:appearance-none
              [&::-webkit-outer-spin-button]:appearance-none'
          />
        </div>
      </div>
    );
  }
);

TablePagination.displayName = 'TablePagination';
